/**
 * ADR-0239 — Dashboard "Install update available" banner.
 *
 * Rendered on the site Overview ONLY when BOTH:
 *   1. The site carries `install_update_available = true` (an active
 *      Worker/plugin install whose observed template version is older
 *      than the current one, OR the response header is absent on a
 *      site Sill has independent evidence runs a Sill install).
 *   2. The install coverage card reports a non-null `action` — some
 *      part of the install is NOT live. That is: the outdated install
 *      is user-visible (something is missing or waiting), not merely
 *      "newer features sitting unused".
 *
 * The banner is dismissible per site + per version via `localStorage`
 * (`install-update-dismissed:{site_id}:{version}`), so a new version
 * re-arms a previously dismissed banner.
 *
 * Copy is plain, no exclamation marks, no internal language — the
 * merchant sees "newer Sill install available" plus one action button.
 */

import { AlertTriangle, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { Site } from '@sill/types';
import type { InstallCoverage } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import type { SetView } from '../../app/view-router.js';
import { installUpdateCopy } from '../../lib/install-update-copy.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";

export interface InstallUpdateBannerProps {
  readonly site: Site;
  readonly setView: SetView;
}

function dismissKey(site_id: string, version: string): string {
  return `install-update-dismissed:${site_id}:${version}`;
}

function readDismissed(site_id: string, version: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(dismissKey(site_id, version)) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(site_id: string, version: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(dismissKey(site_id, version), '1');
  } catch {
    // best effort; a storage-disabled browser just re-shows the banner
    // on the next load, which is fine (not a surprise, same copy).
  }
}

export function InstallUpdateBanner({ site, setView }: InstallUpdateBannerProps): JSX.Element | null {
  const C = useC();
  const api = useApi();
  const [coverage, setCoverage] = useState<InstallCoverage | null>(null);
  const [failed, setFailed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // The version we read coverage AGAINST — used in the dismiss key so a
  // new release re-arms the banner for sites the merchant previously
  // dismissed.
  const latest = site.install_update?.latest ?? 'pending';

  // Load coverage so we can decide whether the outdated install is
  // visibly broken. We mirror the InstallCoverageCard loader (lightweight
  // GET; fail-soft to null).
  useEffect(() => {
    let alive = true;
    api.sites.getInstallCoverage(site.site_id)
      .then((c) => { if (alive) { setCoverage(c); setFailed(false); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [api, site.site_id]);

  useEffect(() => {
    setDismissed(readDismissed(site.site_id, latest));
  }, [site.site_id, latest]);

  const onDismiss = useCallback(() => {
    writeDismissed(site.site_id, latest);
    setDismissed(true);
  }, [site.site_id, latest]);

  if (site.install_update_available !== true) return null;
  if (failed) return null;
  if (dismissed) return null;
  if (coverage === null) return null;

  // Only show when the install is outdated AND coverage reports a
  // pending action (something is uncovered). Full-green coverage on an
  // outdated install stays silent — the merchant has newer features
  // sitting unused but nothing visible is broken.
  if (coverage.action === null) return null;

  return (
    <div
      data-testid="install-update-banner"
      className="p-3 mb-3 flex items-start gap-2 flex-wrap"
      style={{
        background: C.amberDim,
        border: `1px solid ${C.amberFg}`,
        borderRadius: 2,
      }}
    >
      <AlertTriangle size={13} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
      <div
        className="flex-1 min-w-[260px]"
        style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}
      >
        {site.install_update !== undefined ? (() => {
          const copy = installUpdateCopy(site.install_update);
          return (
            <>
              <strong>{copy.install} update {copy.versions}.</strong>{' '}
              {copy.short} Updating picks up the newest Sill files and fixes.
            </>
          );
        })() : (
          <>
            <strong>Install update available.</strong>{' '}
            A newer Sill install is out. Open install settings for the steps.
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          try {
            const url = new URL(window.location.href);
            url.searchParams.set('settings_tab', 'install');
            url.searchParams.set('settings_section', 'install-update');
            window.history.replaceState({}, '', url.toString());
          } catch {
            // Best-effort; Settings opens on its default tab.
          }
          setView('settings');
        }}
        className="flex-shrink-0 px-3 py-1.5"
        style={{
          background: C.amber,
          color: C.ink,
          fontFamily: sansFont,
          fontSize: 12,
          fontWeight: 600,
          borderRadius: 2,
          border: 'none',
        }}
      >
        How to update
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="flex-shrink-0 flex items-center justify-center"
        style={{
          width: 24,
          height: 24,
          background: 'transparent',
          border: 'none',
          color: C.muted,
          cursor: 'pointer',
        }}
      >
        <X size={13} />
      </button>
    </div>
  );
}
